import React, { type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/shared/lib/cn";

export type LoadingSize = "xs" | "sm" | "md" | "lg" | "xl";
export type LoadingTone =
  | "primary"
  | "neutral"
  | "on-primary"
  | "danger"
  | "warning"
  | "success"
  | "inherit";

export interface SpinnerProps {
  /** Kích thước icon xoay (xs=14px, sm=16px, md=20px, lg=28px, xl=36px) */
  size?: LoadingSize;
  /** Tone màu theo design tokens */
  tone?: LoadingTone;
  className?: string;
  "aria-label"?: string;
}

const SIZE_MAP: Record<LoadingSize, string> = {
  xs: "size-3.5",
  sm: "size-4",
  md: "size-5",
  lg: "size-7",
  xl: "size-9",
};

const TONE_MAP: Record<LoadingTone, string> = {
  primary: "text-primary",
  neutral: "text-text-muted",
  "on-primary": "text-on-primary",
  danger: "text-danger-fg",
  warning: "text-warning-fg",
  success: "text-success-fg",
  inherit: "text-inherit",
};

/**
 * Spinner Component - Icon quay nạp dữ liệu độc lập
 */
export function Spinner({
  size = "md",
  tone = "primary",
  className,
  "aria-label": ariaLabel = "Đang tải...",
}: SpinnerProps) {
  return (
    <Loader2
      className={cn(
        "animate-spin shrink-0",
        SIZE_MAP[size],
        TONE_MAP[tone],
        className
      )}
      aria-hidden="true"
      data-testid="core-spinner"
    />
  );
}

export interface LoadingProps {
  /** Kích thước spinner */
  size?: LoadingSize;
  /** Tone màu */
  tone?: LoadingTone;
  /** Nhãn mô tả trạng thái tải */
  label?: ReactNode;
  /** Hướng bố trí nhãn văn bản */
  direction?: "horizontal" | "vertical";
  className?: string;
}

/**
 * Loading Component - Khối nạp dữ liệu chuẩn gồm Spinner và text label
 */
export function Loading({
  size = "md",
  tone = "primary",
  label,
  direction = "horizontal",
  className,
}: LoadingProps) {
  const isVertical = direction === "vertical";

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "inline-flex items-center justify-center gap-2 text-caption select-none",
        isVertical ? "flex-col" : "flex-row",
        className
      )}
    >
      <Spinner size={size} tone={tone} />
      {label ? (
        <span className="text-text-secondary font-medium tracking-normal">
          {label}
        </span>
      ) : (
        <span className="sr-only">Đang tải dữ liệu...</span>
      )}
    </div>
  );
}

export interface LoadingOverlayProps {
  /** Có hiển thị lớp phủ hay không */
  visible?: boolean;
  /** Kích thước spinner */
  size?: LoadingSize;
  /** Tone màu */
  tone?: LoadingTone;
  /** Nhãn hiển thị */
  label?: ReactNode;
  /** Lớp phủ có làm mờ nhẹ nền không */
  blur?: boolean;
  className?: string;
}

/**
 * LoadingOverlay Component - Lớp phủ mờ bán trong suốt dùng cho Table, Card, Modal khi đang fetch dữ liệu
 */
export function LoadingOverlay({
  visible = true,
  size = "md",
  tone = "primary",
  label = "Đang tải dữ liệu…",
  blur = true,
  className,
}: LoadingOverlayProps) {
  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className={cn(
        "absolute inset-0 z-20 flex flex-col items-center justify-center p-4 transition-all duration-200 rounded-[inherit]",
        "bg-surface/80",
        blur && "backdrop-blur-[2px]",
        className
      )}
    >
      <div className="rounded-lg bg-surface/95 border border-border/80 px-4 py-3 shadow-md">
        <Loading size={size} tone={tone} label={label} direction="vertical" />
      </div>
    </div>
  );
}

/**
 * IndeterminateProgressBar - Thanh chạy ngang thanh mảnh đặt ở mép trên Table khi fetch trang
 */
export function IndeterminateProgressBar({
  className,
}: {
  className?: string;
}) {
  return (
    <div
      role="progressbar"
      aria-label="Đang nạp trang dữ liệu"
      aria-valuetext="indeterminate"
      className={cn(
        "relative h-0.5 w-full overflow-hidden bg-primary/15",
        className
      )}
    >
      <div className="absolute inset-y-0 left-0 h-full w-1/3 bg-primary animate-pulse rounded-full" />
    </div>
  );
}
